import { useMemo } from 'react';
import useSelectLocation from '../../../../../../../hooks/useSelectLocation';
import {
  isMobileCheckInEnabled,
  isPaperworkEnabled,
} from '../../../../../../../../../core/util/location';
import { BookingStatus } from '../../../../../../../../../constants/index';

import {
  hasAssociatedLabResults,
  hasSubmittedRating,
  isBookingWithin,
  isExternalTelemedBooking,
  isPremiumBooking,
  isProviderGroupBooking,
  isTelemedBooking,
} from '../../../../../../../../../core/util/booking';
import {
  ActiveBookingStates,
  isPaperworkCompleteOrNotApplicable,
} from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/util';
import { useVisitSummary } from '~/components/AccountGetsMarried/components/GetCare/hooks/useVisitSummary';
import { VisitSummaryStatuses } from '~/components/AccountGetsMarried/constants/visitSummary';

/**
 * Get the active booking state based on appointment status, and booking and location properties
 *
 * @param {any} appointment - a booking
 * @returns {number} - an ActiveBookingState
 */
export const useActiveBookingState = (appointment: any) => {
  const location = useSelectLocation(appointment?.location_id);
  const { summaryStatus } = useVisitSummary(appointment?.id);

  return useMemo(() => {
    if (!appointment) return -1;

    const isTelemedAppt = isTelemedBooking(appointment);
    const isExternalTelemed = isExternalTelemedBooking(appointment);
    const isProviderGroup = isProviderGroupBooking(appointment);
    const paperworkEnabled = isPaperworkEnabled(location);
    const hasLabResults = hasAssociatedLabResults(appointment);
    const withinTelemedVisitTime = isBookingWithin(appointment, 5, 'minutes');
    const withinClinicVisitTime = isBookingWithin(appointment, 30, 'minutes');
    const preVisit = BookingStatus.isPreVisit(appointment.status);
    const inVisit = BookingStatus.isInVisit(appointment.status);
    const mobileCheckInEnabled = isMobileCheckInEnabled(location);
    const paperworkCompleteOrNotApplicable = isPaperworkCompleteOrNotApplicable(
      appointment,
      location
    );
    const submittedRating = hasSubmittedRating(appointment);
    const isPremiumVisit = isPremiumBooking(appointment);

    if (preVisit) {
      if (mobileCheckInEnabled) {
        if (withinClinicVisitTime || paperworkCompleteOrNotApplicable) {
          return ActiveBookingStates.PreClinicVisitHasCheckCompleteOrApptSoon;
        }
        return ActiveBookingStates.PreClinicVisitHasCheckIncompleteAndEarly;
      }
      if (isTelemedAppt || isExternalTelemed) {
        if (!isProviderGroup && isExternalTelemed) {
          return ActiveBookingStates.PreExternalTelemed;
        }

        if (paperworkCompleteOrNotApplicable) {
          if (withinTelemedVisitTime) {
            return ActiveBookingStates.PreTelemedCompleteAndApptSoon;
          }
          return ActiveBookingStates.PreTelemedCompleteAndEarly;
        }
        if (withinTelemedVisitTime) {
          return ActiveBookingStates.PreTelemedIncompleteAndApptSoon;
        }
        return ActiveBookingStates.PreTelemedIncompleteAndEarly;
      }
      //No mobile check in, no vid visit

      if (paperworkCompleteOrNotApplicable) {
        if (paperworkEnabled) {
          return ActiveBookingStates.PreClinicVisitNoCheckComplete;
        }
        return ActiveBookingStates.PreClinicVisitNoCheckNAPaperwork;
      }
      return ActiveBookingStates.PreClinicVisitNoCheckIncomplete;
    }

    if (inVisit) {
      if (isTelemedAppt || isExternalTelemed) return ActiveBookingStates.InTelemed;
      return ActiveBookingStates.InClinicVisit;
    }

    //Post-visit states
    if (hasLabResults) {
      return ActiveBookingStates.PostVisitHasLabResult;
    }

    if (isPremiumVisit) {
      switch (summaryStatus) {
        case VisitSummaryStatuses.Downloadable:
          return ActiveBookingStates.PostTelemedSummaryDownloadable;
        case VisitSummaryStatuses.Updatable:
          return ActiveBookingStates.PostTelemedSummaryUpdatable;
        case VisitSummaryStatuses.Viewable:
          return ActiveBookingStates.PostTelemedSummaryViewable;
        case VisitSummaryStatuses.Pending:
          return ActiveBookingStates.PostTelemedSummaryPending;
        case VisitSummaryStatuses.Missing:
        default:
          return ActiveBookingStates.PostTelemedSummaryMissing;
      }
    }
    if (submittedRating) {
      if (isTelemedAppt || isExternalTelemed) return ActiveBookingStates.PostTelemedHasRating;
      return ActiveBookingStates.PostClinicVisitHasRating;
    }
    if (isTelemedAppt || isExternalTelemed) return ActiveBookingStates.PostTelemedNoRating;
    return ActiveBookingStates.PostClinicVisitNoRating;
  }, [appointment, location, summaryStatus]);
};
